import { useTheme } from 'next-themes';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CurveSettings, RootDispatch } from '@/components/WellLoggingOperations/types';
import { Button } from '@/shared/MiButton/MiButton';
import { Select } from '@/shared/MiSelect/MiSelect';
import Close from '@/shared/Modal/icons';
import { PickerColors } from '@/shared/PickerColors';
import { useTabletSettings } from '../TabletSettingsProvider';
import {
    BallLine,
    BoldBallLine,
    BoldCircleLine,
    BoldLine,
    CircleLine,
    MediumLine,
    NormalLine,
    RectLine,
    SemiBoldLine,
    TransparentRectLine,
} from './icons';
import { DashedLine } from './icons/DashedLine';
import { DottedLine } from './icons/DottedLine';

export const LINES = {
    NormalLine: {
        lineType: 0,
        lineWidth: 1,
        lineDisplay: '',
        value: 1,
    },
    MediumLine: {
        lineType: 0,
        lineWidth: 2,
        lineDisplay: '',
        value: 2,
    },
    SemiBoldLine: {
        lineType: 0,
        lineWidth: 3,
        lineDisplay: '',
        value: 3,
    },
    BoldLine: {
        lineType: 0,
        lineWidth: 4,
        lineDisplay: '',
        value: 4,
    },
    DashedLine: {
        lineType: 0,
        lineWidth: 1,
        lineDisplay: 'dashed',
        value: 11,
    },
    DottedLine: {
        lineType: 0,
        lineWidth: 1,
        lineDisplay: 'dotted',
        value: 12,
    },
    BallLine: {
        lineType: 1,
        lineWidth: 1,
        lineDisplay: 'ball',
        value: 5,
    },
    BoldBallLine: {
        lineType: 1,
        lineWidth: 2,
        lineDisplay: 'ball',
        value: 6,
    },
    CircleLine: {
        lineType: 1,
        lineWidth: 1,
        lineDisplay: 'circle',
        value: 7,
    },
    BoldCircleLine: {
        lineType: 1,
        lineWidth: 2,
        lineDisplay: 'circle',
        value: 8,
    },
    RectLine: {
        lineType: 1,
        lineWidth: 2,
        lineDisplay: 'rectangle',
        value: 9,
    },
    TransparentRectLine: {
        lineType: 1,
        lineWidth: 2,
        lineDisplay: 'transparentRect',
        value: 10,
    },
} as const;

const columnOptions = [
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
    { value: 6 },
    { value: 7 },
    { value: 8 },
    { value: 9 },
    { value: 10 },
];

export const useLineOptions = () => {
    const { theme } = useTheme();

    const result = useMemo(() => {
        const color = theme === 'dark' ? '#F9FAFB' : '#5D607B';

        return [
            {
                label: (
                    <div className="pt-3">
                        <NormalLine color={color} />
                    </div>
                ),
                key: 1,
                value: 1,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <MediumLine color={color} />
                    </div>
                ),
                key: 2,
                value: 2,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <SemiBoldLine color={color} />
                    </div>
                ),
                key: 3,
                value: 3,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <BoldLine color={color} height={4} />
                    </div>
                ),
                key: 4,
                value: 4,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <DashedLine color={color} height={4} />
                    </div>
                ),
                key: 11,
                value: 11,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <DottedLine color={color} height={4} />
                    </div>
                ),
                key: 12,
                value: 12,
            },
            {
                label: (
                    <div className="pt-3">
                        <BallLine color={color} height={1} />
                    </div>
                ),
                key: 5,
                value: 5,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <BoldBallLine color={color} />
                    </div>
                ),
                key: 6,
                value: 6,
            },
            {
                label: (
                    <div className="pt-3">
                        <CircleLine color={color} height={1} />
                    </div>
                ),
                key: 7,
                value: 7,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <BoldCircleLine color={color} />
                    </div>
                ),
                key: 8,
                value: 8,
            },
            {
                label: (
                    <div className="pt-3">
                        <RectLine color={color} height={1} />
                    </div>
                ),
                key: 9,
                value: 9,
            },
            {
                label: (
                    <div className="pt-3.5">
                        <TransparentRectLine color={color} />
                    </div>
                ),
                key: 10,
                value: 10,
            },
        ];
    }, [theme]);

    return result;
};

export const findLineType = (item: CurveSettings): number => {
    const lineType = Object.values(LINES).find(line => {
        return (
            line.lineType === item.lineType &&
            line.lineWidth === item.lineWidth &&
            line.lineDisplay === item.lineDisplay
        );
    });

    if (lineType) {
        return lineType.value;
    }

    return LINES.NormalLine.value;
};

const findLineByValue = (value: number) => {
    const line = Object.values(LINES).find(line => line.value === value);

    return line ?? LINES.NormalLine;
};

interface CurveSettingsModalProps {
    researchKey: string;
    curve: number;
    data: CurveSettings;
    onCancel: () => void;
}
export const CurveSettingsModal: FC<CurveSettingsModalProps> = ({ researchKey, curve, data, onCancel }) => {
    const { tabletSettings, updateTabletSettings } = useTabletSettings();
    const dispatch = useDispatch<RootDispatch>();
    //@ts-ignore
    const curves = useSelector(state => state.curves.data);

    const [color, setColor] = useState(data.color);
    const [columnNum, setColumnNum] = useState(1);
    const [lineType, setLineType] = useState(() => findLineType(data));

    const lineOptions = useLineOptions();

    const saveData = () => {
        const line = findLineByValue(lineType);

        const updatedCurveSettings = {
            ...data,
            color,
            lineWidth: line.lineWidth,
            lineType: line.lineType,
            lineDisplay: line.lineDisplay,
            columnNum,
        };

        updateTabletSettings({
            ...tabletSettings,
            curveSettings: {
                ...tabletSettings.curveSettings,
                [researchKey]: updatedCurveSettings,
            },
        });

        if (Object.keys(curves).includes(curve.toString())) {
            const newCurves: Record<number, any> = {};
            Object.keys(curves).forEach(curveId => {
                const compare1 = curveId.toString().replace('00001', '').replace('00002', '');
                const compare2 = curve.toString().replace('00001', '').replace('00002', '');
                if (compare1.startsWith(compare2) || compare2.startsWith(compare1)) {
                    newCurves[Number(curveId)] = {
                        ...curves[curveId],
                        columnIndex: columnNum - 1,
                    };
                } else {
                    newCurves[Number(curveId)] = { ...curves[curveId] };
                }
            });
            dispatch.curves.setCurves(newCurves);
        } else {
            dispatch.curves.loadCurves({ ids: [curve], columnIndex: columnNum - 1 });
        }

        // @ts-ignore

        onCancel();
    };

    useEffect(() => {
        setColumnNum(curves[curve]?.columnIndex ? curves[curve].columnIndex + 1 : 1);
    }, [curves, curve]);

    return (
        <div className="flex flex-col rounded border border-solid border-gray-500 bg-white dark:border-block-primary dark:bg-block-secondary">
            <div className="flex items-center justify-between border-b border-gray-500 bg-gray-400 px-4 dark:border-block-primary dark:bg-transparent">
                <span className="text-xs font-semibold text-black dark:text-font-secondary">
                    {data.title} {data.code}
                </span>

                <span className="-mr-1 cursor-pointer p-2" onClick={onCancel}>
                    <Close className="stroke-current text-font-secondary hover:text-font-tertiary" />
                </span>
            </div>

            <div className="flex gap-x-2 px-4 pt-4">
                <div className="flex flex-col">
                    <span className="my-1 text-xs font-medium text-black dark:text-font-quarternary">Колонка</span>
                    <Select // @ts-ignore
                        style={{ width: 70 }}
                        styles={{
                            popup: {
                                root: { zIndex: 10000 },
                            },
                        }}
                        options={columnOptions}
                        value={columnNum}
                        onChange={(value: number) => setColumnNum(value)}
                    />
                </div>

                <div className="flex flex-col">
                    <span className="my-1 text-xs font-medium text-black dark:text-font-quarternary">Линия</span>
                    <Select // @ts-ignore
                        style={{ width: '130px' }}
                        styles={{
                            popup: {
                                root: { zIndex: 10000 },
                            },
                        }}
                        options={lineOptions}
                        value={lineType}
                        onChange={setLineType}
                    />
                </div>
            </div>

            <div className="px-4">
                <PickerColors currentColor={color} setCurrentColor={setColor} />
            </div>

            <div className="flex justify-end gap-x-2 border-t border-gray-500 p-4 dark:border-block-primary">
                <Button onClick={onCancel}>Отменить</Button>
                <Button type="primary" onClick={saveData}>
                    Сохранить
                </Button>
            </div>
        </div>
    );
};
